import {
  Badge,
  Box,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Tab,
  VStack,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import { DistributionTiersBreakdown } from "features/distributions/DistributionTiersBreakdown";
import { HypotheticalTabProps } from "features/financial-reporting/tabs/HypotheticalTabProps";
import { isInfoPotentialClawback } from "features/financial-reporting/utils";
import {
  useFundHypotheticalWaterfallCarryClawbackRetrieve,
  useHypotheticalWaterfallDetailsBreakdownRetrieve,
  useHypotheticalWaterfallNavBreakdownRetrieveSuspense,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { GenericTableData } from "gen/model/genericTableData";
import { WaterfallTierTypeEnum } from "gen/model/waterfallTierTypeEnum";
import { toLower } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { ClickableElementGenericLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import { LoadingState } from "shared/components/empty/LoadingState";
import { FundDropdown } from "shared/components/input/dropdowns/FundDropdown";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const HypotheticalTabWaterfallDetails: React.FC = () => {
  const {
    fundFamilyId,
    hypotheticalWaterfallId,
    endOfPeriodReportId,
    fundId,
    setFundId,
  } = useOutletContext<HypotheticalTabProps>();

  const { data: funds } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const { t } = useTranslation(["common", "financialReporting"]);

  const { data: hypotheticalFundData, isLoading: isLoadingDetailsBreakdown } =
    useHypotheticalWaterfallDetailsBreakdownRetrieve(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundId,
      },
    );

  const { data: clawbackData, isLoading: isLoadingCarryClawback } =
    useFundHypotheticalWaterfallCarryClawbackRetrieve(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
    );

  const fundIdToClawBackInfo =
    clawbackData?.fund_id_to_hypothetical_clawback_info;

  const clawbackInfo = fundIdToClawBackInfo
    ? fundIdToClawBackInfo[fundId]
    : null;

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const baseRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .financialReporting({});
      const renderedValue = defaultDataTableRenderCellValue(
        props,
        fieldDisabled,
        params,
      );
      const { cell } = props;
      if (
        cell.metadata?.type == "waterfall_tier" &&
        cell.metadata.tier_type == WaterfallTierTypeEnum.preferred_return &&
        cell.metadata.waterfall_tier_mapping_id
      ) {
        const linkInfo: ClickableElementGenericLinkInfo = {
          type: "generic",
          variant: "tableCellAction",
          to: baseRoute
            .hypotheticalWaterfall({
              hypotheticalWaterfallId,
              endOfPeriodReportId,
            })
            .prefBreakdown({
              fundEntityId: fundId,
              waterfallTierMappingId: cell.metadata.waterfall_tier_mapping_id,
            }).$,
        };
        return (
          <>
            {renderedValue}
            <DataTableCellLink
              tableContext={props.tableContext}
              columnContext={props.columnContext}
              row={props.row}
              linkInfo={linkInfo}
              onButtonClick={undefined}
            >
              {t("common:viewDetails")}
            </DataTableCellLink>
          </>
        );
      }
      if (cell.metadata?.type == "investor_entity") {
        const linkInfo: ClickableElementGenericLinkInfo = {
          type: "generic",
          variant: "tableCellAction",
          to: baseRoute
            .hypotheticalWaterfall({
              hypotheticalWaterfallId,
              endOfPeriodReportId,
            })
            .investorWaterfallDetails({
              fundEntityId: cell.metadata.fund_id,
              investorEntityId: cell.metadata.investor_entity_id,
            }).$,
        };
        return (
          <DataTableCellLink
            tableContext={props.tableContext}
            columnContext={props.columnContext}
            row={props.row}
            linkInfo={linkInfo}
            onButtonClick={undefined}
          >
            {renderedValue}
          </DataTableCellLink>
        );
      }
      return renderedValue;
    },
    [endOfPeriodReportId, fundFamilyId, fundId, hypotheticalWaterfallId, t],
  );

  const { data: hypotheticalNavData, isLoading: isNavBreakdownLoading } =
    useHypotheticalWaterfallNavBreakdownRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        get_itd_only: true,
      },
    );
  const isLoading =
    isLoadingDetailsBreakdown ||
    isLoadingCarryClawback ||
    !clawbackInfo ||
    !hypotheticalFundData;

  const tableData = [
    hypotheticalFundData?.waterfall_results_table,
    hypotheticalNavData.itd_table_data,
  ];

  const amountSubjectToClawback = Decimal.max(
    decimalOrZero(clawbackInfo?.amount_subject_to_clawback).sub(
      decimalOrZero(clawbackInfo?.protected_carry),
    ),
    decimalOrZero(null),
  );

  return (
    <PageTabBody flex={1}>
      {isLoading && (
        <LoadingState
          height="100%"
          width="100%"
          message={t("financialReporting:loading", {
            item: toLower(t("financialReporting:waterfallDetails")),
          })}
          display="flex"
        />
      )}
      {!isLoading && (
        <VStack alignItems="stretch" spacing="0">
          <Box alignSelf="flex-end">
            <FundDropdown
              fundId={fundId}
              onChangeFundId={setFundId}
              canSelectAllFunds={false}
              funds={funds}
            />
          </Box>
          <TableBox>
            <TableBoxHeader>
              <TableBoxTabs index={selectedTab} onChange={setSelectedTab}>
                <Tab>
                  {t("financialReporting:waterfallDetailsTable.tabs.fundLevel")}
                </Tab>
                <Tab>
                  {t(
                    "financialReporting:waterfallDetailsTable.tabs.investorLevel",
                  )}
                </Tab>
              </TableBoxTabs>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <DataTable
                renderCellValue={renderCellValue}
                key={selectedTab}
                isLoading={isNavBreakdownLoading}
                data={tableData[selectedTab]!}
              />
            </TableBoxBody>
          </TableBox>
          <DistributionTiersBreakdown data={hypotheticalFundData} />
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>{t("common:carry")}</TableBoxTitle>
              <TableBoxBadges>
                {amountSubjectToClawback.gt(decimalOrZero(null)) && (
                  <Badge colorScheme="error">
                    {t(
                      "financialReporting:hypoProtectedCarry.potentialClawback.badge",
                    )}
                  </Badge>
                )}
              </TableBoxBadges>
              <DataTableBoxActions />
            </TableBoxHeader>
            {isInfoPotentialClawback(clawbackInfo) && (
              <TableBoxStats>
                <StatGroup>
                  <Stat>
                    <StatLabel>
                      {t(
                        "financialReporting:hypoProtectedCarry.stats.maximumClawbackAmount",
                      )}
                    </StatLabel>
                    <StatNumber fontSize="20px">
                      {formatMoney(clawbackInfo.amount_subject_to_clawback)}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>
                      {t(
                        "financialReporting:hypoProtectedCarry.stats.carryProtectedFromClawBack",
                      )}
                    </StatLabel>
                    <StatNumber fontSize="20px">
                      {formatMoney(clawbackInfo.protected_carry)}
                    </StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>
                      {t(
                        "financialReporting:hypoProtectedCarry.stats.amountSubjectToClawback",
                      )}
                    </StatLabel>
                    <StatNumber fontSize="20px">
                      {formatMoney(amountSubjectToClawback.toString())}
                    </StatNumber>
                  </Stat>
                </StatGroup>
              </TableBoxStats>
            )}
            <TableBoxBody>
              <DataTable
                isLoading={isNavBreakdownLoading}
                data={
                  hypotheticalFundData.carry_breakdown_table as GenericTableData
                }
              />
            </TableBoxBody>
          </TableBox>
        </VStack>
      )}
    </PageTabBody>
  );
};
